import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useRealtime } from '../../contexts/RealtimeContext';
import {
  kaizenAutoHeightTransition,
  kaizenDialogTransition,
  kaizenStateTransition
} from '../../lib/kaizenMotion';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { JobArtifact } from '@kaizen/shared';
import { FileViewerModal } from '../modals/FileViewerModal';
import { formatBytes } from '../../lib/utils';
import { 
  Terminal, 
  Trash2, 
  Copy, 
  Check, 
  Square, 
  Clock, 
  ArrowDown, 
  CheckCircle,
  XCircle,
  Download,
  FileText,
  Eye,
  Loader2,
  AlertCircle,
  WifiOff,
  MoreHorizontal,
  Bot
} from 'lucide-react';
import { StatusBadge } from '../common/StatusBadge';

const SUPPORTED_TEXT_EXTENSIONS = new Set([
  '.txt',
  '.log',
  '.json',
  '.md',
  '.csv',
  '.xml',
  '.yaml',
  '.yml'
]);

function isSupportedTextArtifact(filename: string): boolean {
  const ext = ('.' + filename.split('.').pop() || '').toLowerCase();
  return SUPPORTED_TEXT_EXTENSIONS.has(ext);
}

interface TerminalViewProps {
  title?: string;
  maxHeight?: string;
  isCompact?: boolean;

  /*
   * Console V3 integration hooks.
   *
   * Defaults preserve every other TerminalView usage.
   */
  fillAvailable?: boolean;
  headerAccessory?: React.ReactNode;
  footer?: React.ReactNode;
}

export const TerminalView: React.FC<TerminalViewProps> = ({
  title = 'Live Console',
  maxHeight = 'h-[460px]',
  isCompact = false,
  fillAvailable = false,
  headerAccessory,
  footer
}) => {
  const navigate =
    useNavigate();
  const {
    activeJob,
    activeJobLogs,
    clearLogs,
    cancelActiveJob,
    getArtifactContent,
    readTextFile,
    device,
    sendJarvisMessage,
    deviceStatus
  } = useRealtime();

  const [showTimestamps, setShowTimestamps] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [copiedLogs, setCopiedLogs] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  /*
   * Mobile-friendly terminal clear gesture.
   *
   * Double-tap the output area to clear it.
   * Desktop double-click performs the same action.
   */
  const lastTerminalTapRef =
    useRef(
      0
    );


  const handleTerminalDoubleTap =
    () => {

      const now =
        Date.now();


      if (
        now -
          lastTerminalTapRef.current <
        340
      ) {

        lastTerminalTapRef.current =
          0;

        clearLogs();

        return;
      }


      lastTerminalTapRef.current =
        now;
    };

  // Artifact quick actions state
  const [artifacts, setArtifacts] = useState<JobArtifact[]>([]);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [copyingReport, setCopyingReport] = useState(false);
  const [copiedReport, setCopiedReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const retryTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  /*
   * Artifact state must belong ONLY to the current job.
   *
   * Without this guard a completed report from the previous job can
   * remain visible while a new job is executing or when the new job
   * produces no artifacts.
   */
  const artifactJobIdRef = useRef<string | null>(null);

  // Autoscroll logic
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeJobLogs, autoScroll]);

  // Query artifacts for the active/recent job with bounded retry on completion
  useEffect(() => {
    retryTimeoutsRef.current.forEach(clearTimeout);
    retryTimeoutsRef.current = [];

    const currentJobId = activeJob?.id;

    /*
     * A new job owns a completely fresh artifact state.
     *
     * This is intentionally done BEFORE querying Supabase so the
     * previous job's report never remains visible during the lookup.
     */
    if (
      artifactJobIdRef.current !==
      (currentJobId || null)
    ) {
      artifactJobIdRef.current =
        currentJobId || null;

      setArtifacts([]);
      setSelectedArtifactId(null);
      setIsViewerOpen(false);
      setCopiedReport(false);
      setReportError(null);
    }
    if (!currentJobId) {
      setArtifacts([]);
      setSelectedArtifactId(null);
      return;
    }

    let isSubscribed = true;

    const fetchArtifacts = async (retryCount: number) => {
      if (!isSupabaseConfigured || !currentJobId) return;

      try {
        const { data, error } = await supabase
          .from('kr_job_artifacts')
          .select('*')
          .eq('job_id', currentJobId)
          .order('created_at', { ascending: false });

        if (!isSubscribed) return;

        if (!error && data && data.length > 0) {
          const mapped: JobArtifact[] = data
            .map((row: any) => ({
              id: row.id,
              jobId: row.job_id,
              projectId: row.project_id || undefined,
              filename: row.filename,
              localPath: row.local_path,
              mimeType: row.mime_type || 'text/plain',
              size: row.size || 0,
              contentPreview: row.content_preview || undefined,
              createdAt: row.created_at
            }))
            .filter((art: JobArtifact) => isSupportedTextArtifact(art.filename));

          if (mapped.length > 0) {
            setArtifacts(mapped);
            setSelectedArtifactId(prev => {
              if (prev && mapped.some(m => m.id === prev)) return prev;
              return mapped[0].id;
            });
            return;
          }
        }

        /*
         * Successful lookup with no supported artifacts means this
         * job has NO artifact. Never preserve a previous job's file.
         */
        if (
          !error &&
          isSubscribed
        ) {
          setArtifacts([]);
          setSelectedArtifactId(null);
        }

        // Bounded retry if terminal state and zero artifacts found yet
        const isTerminal =
          activeJob?.status === 'succeeded' ||
          activeJob?.status === 'failed' ||
          activeJob?.status === 'cancelled' ||
          activeJob?.status === 'timed_out';

        if (isTerminal && isSubscribed) {
          if (retryCount === 0) {
            const t1 = setTimeout(() => {
              void fetchArtifacts(1);
            }, 500);
            retryTimeoutsRef.current.push(t1);
          } else if (retryCount === 1) {
            const t2 = setTimeout(() => {
              void fetchArtifacts(2);
            }, 1500);
            retryTimeoutsRef.current.push(t2);
          }
        }
      } catch {
        // Suppress background lookup failures
      }
    };

    void fetchArtifacts(0);

    return () => {
      isSubscribed = false;
      retryTimeoutsRef.current.forEach(clearTimeout);
      retryTimeoutsRef.current = [];
    };
  }, [activeJob?.id, activeJob?.status]);

  const selectedArtifact = useMemo(() => {
    if (artifacts.length === 0) return null;
    return artifacts.find(a => a.id === selectedArtifactId) || artifacts[0];
  }, [artifacts, selectedArtifactId]);

  /*
   * Current job output is itself useful remotely even when a script
   * did not register a formal artifact.
   *
   * This allows the phone UI to Copy / Save the complete execution
   * output without touching the Windows clipboard.
   */
  const terminalOutputText =
    useMemo(
      () =>
        activeJobLogs
          .map(
            line =>
              line.data
          )
          .join(
            ''
          ),
      [
        activeJobLogs
      ]
    );

  const terminalOutputBytes =
    useMemo(
      () =>
        new Blob(
          [
            terminalOutputText
          ]
        ).size,
      [
        terminalOutputText
      ]
    );

  /*
   * Fallback report discovery.
   *
   * Formal JobArtifact records remain preferred.
   * This handles older PowerShell scripts which merely print:
   *
   *   F:\Somewhere\REPORT.txt
   *
   * rather than explicitly registering the generated file.
   */
  const kaizenDetectedReportPath =
    useMemo(
      () => {

        const matches =
          [
            ...terminalOutputText.matchAll(
              /([A-Za-z]:\\[^<>"|?*\r\n]+?\.(?:txt|log|json|md|csv|xml|yaml|yml))/gi
            )
          ];


        if (
          matches.length ===
          0
        ) {

          return null;
        }


        return (
          matches[
            matches.length -
            1
          ][1]
            ?.trim()
            .replace(
              /['"`,;:\]\)]+$/,
              ''
            ) ||
          null
        );
      },
      [
        terminalOutputText
      ]
    );


  /*
   * KAIZEN SILENT REPORT V1.2
   *
   * Generated reports are prefetched over the device realtime
   * channel. This never creates an execution job.
   */

  const [
    kaizenPathReportName,
    setKaizenPathReportName
  ] =
    useState(
      ''
    );


  const [
    kaizenPathReportSnapshot,
    setKaizenPathReportSnapshot
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const [
    kaizenLoadedReportText,
    setKaizenLoadedReportText
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const kaizenCopyText =
    async (
      text:
        string
    ) => {

      try {

        await navigator.clipboard.writeText(
          text
        );

        return true;

      }
      catch {

        const area =
          document.createElement(
            'textarea'
          );


        area.value =
          text;

        area.style.position =
          'fixed';

        area.style.opacity =
          '0';

        area.style.pointerEvents =
          'none';


        document.body.appendChild(
          area
        );


        area.focus();

        area.select();


        let copied =
          false;


        try {

          copied =
            document.execCommand(
              'copy'
            );

        }
        catch {

          copied =
            false;
        }


        document.body.removeChild(
          area
        );


        return copied;
      }
    };


  const kaizenDownloadText =
    (
      filename:
        string,

      text:
        string
    ) => {

      const blob =
        new Blob(
          [
            text
          ],
          {
            type:
              'text/plain;charset=utf-8'
          }
        );


      const url =
        URL.createObjectURL(
          blob
        );


      const anchor =
        document.createElement(
          'a'
        );


      anchor.href =
        url;

      anchor.download =
        filename ||
        'KAIZEN_REPORT.txt';


      anchor.click();


      URL.revokeObjectURL(
        url
      );
    };


  /*
   * Background prefetch.
   *
   * copyingReport is deliberately used here so the existing report
   * Copy icon shows its Loader2 animation while reading.
   */
  useEffect(
    () => {

      let cancelled =
        false;


      const jobId =
        activeJob?.id;

      const reportPath =
        kaizenDetectedReportPath;


      const finished =
        Boolean(
          activeJob &&
          [
            'succeeded',
            'failed',
            'cancelled',
            'timed_out'
          ].includes(
            activeJob.status
          )
        );


      if (
        selectedArtifact
      ) {

        return () => {

          cancelled =
            true;
        };
      }


      if (
        !jobId ||
        !reportPath
      ) {

        setKaizenPathReportSnapshot(
          null
        );

        setKaizenPathReportName(
          ''
        );

        setKaizenLoadedReportText(
          null
        );


        return () => {

          cancelled =
            true;
        };
      }


      const filename =
        reportPath
          .split(
            /[\\/]/
          )
          .filter(
            Boolean
          )
          .pop() ||
        'KAIZEN_REPORT.txt';


      setKaizenPathReportSnapshot(
        reportPath
      );

      setKaizenPathReportName(
        filename
      );


      if (
        !finished
      ) {

        return () => {

          cancelled =
            true;
        };
      }


      setKaizenLoadedReportText(
        null
      );

      setReportError(
        null
      );

      setCopyingReport(
        true
      );


      void readTextFile(
        reportPath,
        jobId
      )
        .then(
          result => {

            if (
              cancelled
            ) {
              return;
            }


            if (
              !result.success ||
              typeof result.content !==
                'string'
            ) {

              throw new Error(
                result.error ||
                'Generated report could not be read'
              );
            }


            setKaizenPathReportSnapshot(
              result.path
            );

            setKaizenPathReportName(
              result.filename ||
              filename
            );

            setKaizenLoadedReportText(
              result.content
            );

            setReportError(
              null
            );
          }
        )
        .catch(
          error => {

            if (
              cancelled
            ) {
              return;
            }


            setKaizenLoadedReportText(
              null
            );

            setReportError(
              error instanceof Error
                ? error.message
                : String(
                    error
                  )
            );
          }
        )
        .finally(
          () => {

            if (
              !cancelled
            ) {

              setCopyingReport(
                false
              );
            }
          }
        );


      return () => {

        cancelled =
          true;
      };

    },
    [
      activeJob?.id,
      activeJob?.status,
      kaizenDetectedReportPath,
      selectedArtifact?.id,
      readTextFile
    ]
  );


  const handleKaizenPathReport =
    async (
      mode:
        'copy' |
        'download'
    ) => {

      const reportPath =
        kaizenDetectedReportPath ||
        kaizenPathReportSnapshot;


      if (
        !reportPath ||
        !activeJob?.id
      ) {

        setReportError(
          'No generated report is available.'
        );

        return;
      }


      setReportError(
        null
      );

      setCopiedReport(
        false
      );


      if (
        mode ===
          'copy'
      ) {

        setCopyingReport(
          true
        );
      }
      else {

        setDownloadingReport(
          true
        );
      }


      try {

        let text =
          kaizenLoadedReportText;

        let filename =
          kaizenPathReportName ||
          (
            reportPath
              .split(
                /[\\/]/
              )
              .filter(
                Boolean
              )
              .pop() ||
            'KAIZEN_REPORT.txt'
          );


        /*
         * Normally this is already cached.
         * This is only a safe retry path.
         */
        if (
          text ===
            null
        ) {

          const result =
            await readTextFile(
              reportPath,
              activeJob.id
            );


          if (
            !result.success ||
            typeof result.content !==
              'string'
          ) {

            throw new Error(
              result.error ||
              'Generated report could not be read'
            );
          }


          text =
            result.content;

          filename =
            result.filename ||
            filename;


          setKaizenPathReportSnapshot(
            result.path
          );

          setKaizenPathReportName(
            filename
          );

          setKaizenLoadedReportText(
            text
          );
        }


        if (
          mode ===
            'copy'
        ) {

          const copied =
            await kaizenCopyText(
              text
            );


          if (
            copied
          ) {

            setCopiedReport(
              true
            );


            window.setTimeout(
              () =>
                setCopiedReport(
                  false
                ),
              1800
            );

          }
          else {

            setReportError(
              'Report is ready, but clipboard access was blocked. Tap Copy again.'
            );
          }

        }
        else {

          kaizenDownloadText(
            filename,
            text
          );
        }

      }
      catch (
        error:
          unknown
      ) {

        setReportError(
          error instanceof Error
            ? error.message
            : String(
                error
              )
        );

      }
      finally {

        setCopyingReport(
          false
        );

        setDownloadingReport(
          false
        );
      }
    };

  const kaizenReportAvailable =
    Boolean(
      selectedArtifact ||
      kaizenDetectedReportPath ||
      kaizenLoadedReportText
    );

  // Terminal Copy Logs (Terminal Output ONLY)
  const handleCopyLogs = () => {
    const rawText = activeJobLogs.map(l => l.data).join('');
    navigator.clipboard.writeText(rawText);
    setCopiedLogs(true);
    setTimeout(() => setCopiedLogs(false), 2000);
  };

  // Terminal Download Logs (Terminal Output ONLY)
  const handleDownloadLogs = () => {
    if (activeJobLogs.length === 0) return;
    const rawText = activeJobLogs.map(l => l.data).join('');
    const blob = new Blob([rawText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      `KAIZEN_JOB_${activeJob?.id || Date.now()}_OUTPUT.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStop = async () => {
    setIsStopping(true);
    await cancelActiveJob();
    setTimeout(() => setIsStopping(false), 1500);
  };

  // Artifact Action: Copy Full Report
  const handleCopyReport = async () => {
    if (!selectedArtifact) return;
    setReportError(null);

    if (deviceStatus !== 'online') {
      setReportError('Laptop Agent Offline');
      setTimeout(() => setReportError(null), 3000);
      return;
    }

    setCopyingReport(true);
    try {
      const res = await getArtifactContent(selectedArtifact.id);
      if (res.success && typeof res.content === 'string') {
        await navigator.clipboard.writeText(res.content);
        setCopiedReport(true);
        setTimeout(() => setCopiedReport(false), 2000);
      } else {
        setReportError(res.error || 'Failed to retrieve full report');
        setTimeout(() => setReportError(null), 3000);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Copy report failed';
      setReportError(msg);
      setTimeout(() => setReportError(null), 3000);
    } finally {
      setCopyingReport(false);
    }
  };

  // Artifact Action: Download Full Report
  const handleDownloadReport = async () => {
    if (!selectedArtifact) return;
    setReportError(null);

    if (deviceStatus !== 'online') {
      setReportError('Laptop Agent Offline');
      setTimeout(() => setReportError(null), 3000);
      return;
    }

    setDownloadingReport(true);
    try {
      const res = await getArtifactContent(selectedArtifact.id);
      if (res.success && typeof res.content === 'string') {
        const mime = selectedArtifact.mimeType || 'text/plain;charset=utf-8';
        const blob = new Blob([res.content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedArtifact.filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setReportError(res.error || 'Failed to download report');
        setTimeout(() => setReportError(null), 3000);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Download report failed';
      setReportError(msg);
      setTimeout(() => setReportError(null), 3000);
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleViewReport = () => {
    if (!selectedArtifact) return;
    setIsViewerOpen(true);
  };


  const canShareWithJarvis =
    Boolean(
      activeJob &&
      [
        'succeeded',
        'failed',
        'cancelled',
        'timed_out'
      ].includes(
        activeJob.status
      ) &&
      activeJobLogs.length >
        0
    );


  const handleShareWithJarvis =
    () => {

      if (
        !activeJob ||
        !canShareWithJarvis
      ) {
        return;
      }


      const rawOutput =
        activeJobLogs
          .map(
            log =>
              log.data
          )
          .join(
            ''
          )
          .trim();


      if (
        !rawOutput
      ) {
        return;
      }


      const output =
        rawOutput.length >
          80000
          ? rawOutput.slice(
              -80000
            )
          : rawOutput;


      const message =
        [
          'KAIZEN execution result shared from Console.',
          `Job: ${activeJob.id}`,
          `Type: ${activeJob.type}`,
          `Status: ${activeJob.status}`,
          `Working directory: ${activeJob.workingDirectory}`,
          '',
          '--- BEGIN EXECUTION OUTPUT ---',
          output,
          '--- END EXECUTION OUTPUT ---',
          '',
          'Continue from this execution result.'
        ].join(
          '\n'
        );


      void sendJarvisMessage(
        message,
        {
          projectId:
            activeJob.projectId,
          workingDirectory:
            activeJob.workingDirectory
        }
      ).catch(
        error => {
          console.error(
            'Share with JARVIS failed:',
            error
          );
        }
      );


      navigate(
        '/assistant'
      );
    };

  const isRunning =
    activeJob?.status === 'running' ||
    activeJob?.status === 'accepted';

  const isFinished =
    Boolean(
      activeJob &&
      [
        'succeeded',
        'failed',
        'cancelled',
        'timed_out'
      ].includes(
        activeJob.status
      )
    );

  /*
   * Formal artifacts take priority.
   *
   * If the current job produced no registered artifact, expose its
   * terminal output directly instead of leaving the operator with
   * nothing useful on the phone.
   */
  /*
   * KAIZEN CONSOLE REPORT PRESENTATION CONTRACT
   *
   * Terminal output remains available after every finished job.
   * A registered text report must NOT replace the compact
   * Execution Output toolbar.
   *
   * Reports are exposed through the compact report icons below.
   */
  const showExecutionOutputCard =
    Boolean(
      isFinished &&
      terminalOutputText.trim().length >
        0
    );


  /*
   * Legacy full-width report actions are intentionally disabled
   * inside TerminalView.
   *
   * The artifact remains registered, selected and retrievable.
   * Only its redundant large Console presentation is suppressed.
   */
  const showAttachedReportQuickActions =
    false;

  return (
    <div
      className={`overflow-hidden border border-white/10 bg-slate-900/82 shadow-[0_28px_90px_rgba(2,6,23,0.40)] backdrop-blur-2xl flex flex-col font-mono text-xs ${
        fillAvailable
          ? 'h-full min-h-0 rounded-[18px]' : 'rounded-[18px]'
      }`}
    >
      {/* Terminal Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 bg-white/[0.025] border-b border-white/[0.07] text-slate-300 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="hidden sm:flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
          </div>
          <span className="text-slate-400 font-sans font-medium text-[11px] sm:text-xs sm:ml-1 flex items-center gap-1.5 truncate min-w-0">
            <Terminal className="w-3.5 h-3.5 text-indigo-400" />
            {title}
          </span>
          {headerAccessory && (
            <div className="min-w-0 ml-1">
              {headerAccessory}
            </div>
          )}

          {activeJob && (
            <div className="flex items-center gap-2 ml-2">
              <span className="hidden sm:inline text-[10px] text-slate-500">[{activeJob.type}]</span>

              <AnimatePresence
                mode="wait"
                initial={false}
              >
                <motion.span
                  key={`terminal-status-${activeJob.status}`}
                  initial={{
                    opacity: 0,
                    scale: 0.92
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.94
                  }}
                  transition={
                    kaizenStateTransition
                  }
                  className="inline-flex"
                >
                  <StatusBadge status={activeJob.status} size="sm" />
                </motion.span>
              </AnimatePresence>

            </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {/* Status & Exit Code */}
          <AnimatePresence initial={false}>
            {activeJob?.exitCode !== undefined && activeJob?.exitCode !== null && (
              <motion.span
                key={`exit-${activeJob.id}-${activeJob.exitCode}`}
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  x: 4
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0
                }}
                exit={{
                  opacity: 0,
                  scale: 0.94
                }}
                transition={
                  kaizenStateTransition
                }
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${
                  activeJob.exitCode === 0
                    ? 'bg-emerald-950/80 text-emerald-300 border-emerald-800'
                    : 'bg-rose-950/80 text-rose-300 border-rose-800'
                }`}
              >
                {activeJob.exitCode === 0
                  ? <CheckCircle className="w-3 h-3" />
                  : <XCircle className="w-3 h-3" />
                }
                Exit: {activeJob.exitCode}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Stop Job Button */}
          <AnimatePresence initial={false}>
            {isRunning && (
              <motion.button
                onClick={handleStop}
                disabled={isStopping}
                initial={{
                  opacity: 0,
                  scale: 0.94
                }}
                animate={{
                  opacity: 1,
                  scale: 1
                }}
                exit={{
                  opacity: 0,
                  scale: 0.94
                }}
                whileTap={{
                  scale: 0.96
                }}
                transition={
                  kaizenStateTransition
                }
                className="flex items-center gap-1 px-2.5 py-1 rounded bg-rose-900/40 text-rose-300 border border-rose-700/60 hover:bg-rose-900/70 transition-colors text-[11px]"
              >
                <Square className="w-3 h-3 fill-current" />
                <span>{isStopping ? 'Stopping...' : 'Stop Task'}</span>
              </motion.button>
            )}
          </AnimatePresence>


          {canShareWithJarvis && (
            <button
              type="button"
              onClick={handleShareWithJarvis}
              className="hidden sm:flex p-1.5 rounded text-indigo-300 hover:text-white hover:bg-indigo-500/15 transition-colors items-center gap-1"
              title="Share completed output with JARVIS"
              aria-label="Share completed output with JARVIS"
            >
              <Bot className="w-3.5 h-3.5" />
              <span className="hidden lg:inline text-[11px]">Share with JARVIS</span>
            </button>
          )}
          {/* Toggle Timestamps */}
          <button
            onClick={() => setShowTimestamps(!showTimestamps)}
            className={`hidden sm:inline-flex p-1.5 rounded transition-colors ${
              showTimestamps ? 'text-indigo-400 bg-slate-800' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
            title="Toggle Timestamps"
          >
            <Clock className="w-3.5 h-3.5" />
          </button>

          {/* Toggle Autoscroll */}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`hidden sm:inline-flex p-1.5 rounded transition-colors ${
              autoScroll ? 'text-indigo-400 bg-slate-800' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
            title={autoScroll ? 'Autoscroll Active' : 'Autoscroll Paused'}
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>

          {/* Download Logs */}
          <button
            onClick={handleDownloadLogs}
            disabled={activeJobLogs.length === 0}
            className="hidden sm:flex p-1.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors items-center gap-1 disabled:opacity-40"
            title="Download Logs"
            aria-label="Download Logs"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden lg:inline text-[11px]">Logs</span>
          </button>

          {/* Copy Logs */}
          <button
            onClick={handleCopyLogs}
            disabled={activeJobLogs.length === 0}
            className="hidden sm:flex p-1.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors items-center gap-1 disabled:opacity-40"
            title="Copy Logs"
            aria-label="Copy Logs"
          >
            {copiedLogs ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="hidden lg:inline text-[11px]">Copy Logs</span>
          </button>

          {/* Clear Logs */}
          <button
            onClick={clearLogs}
            className="hidden sm:inline-flex p-1.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
            title="Clear Terminal Display"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>

          {/* Mobile Toolbar Menu */}
          <div className="relative sm:hidden">

            <motion.button
              type="button"
              onClick={() => setMobileMenuOpen(previous => !previous)}
              whileTap={{
                scale: 0.94
              }}
              transition={
                kaizenStateTransition
              }
              className={`p-2 rounded-md transition-colors ${
                mobileMenuOpen
                  ? 'bg-slate-800 text-slate-100'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              title="Terminal options"
              aria-label="Terminal options"
              aria-expanded={
                mobileMenuOpen
              }
            >

              <motion.span
                className="block"
                animate={{
                  rotate:
                    mobileMenuOpen
                      ? 90
                      : 0
                }}
                transition={
                  kaizenStateTransition
                }
              >
                <MoreHorizontal className="w-4 h-4" />
              </motion.span>

            </motion.button>


            <AnimatePresence initial={false}>

              {mobileMenuOpen && (

                <motion.div
                  key="terminal-mobile-menu"
                  initial={{
                    opacity: 0,
                    y: -6,
                    scale: 0.98
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }}
                  exit={{
                    opacity: 0,
                    y: -4,
                    scale: 0.985
                  }}
                  transition={
                    kaizenDialogTransition
                  }
                  style={{
                    transformOrigin: 'top right'
                  }}
                  className="
                    absolute
                    right-0
                    top-full
                    mt-2
                    z-40
                    w-48
                    overflow-hidden
                    rounded-lg
                    border
                    border-slate-700
                    bg-slate-900
                    shadow-2xl
                    text-[11px]
                  "
                >

                  <motion.button
                    type="button"
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    onClick={() => {
                      setShowTimestamps(previous => !previous);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-slate-800"
                  >
                    <span>Show timestamps</span>

                    <span
                      className={
                        showTimestamps
                          ? 'text-indigo-400'
                          : 'text-slate-600'
                      }
                    >
                      {showTimestamps
                        ? 'On'
                        : 'Off'}
                    </span>

                  </motion.button>


                  <motion.button
                    type="button"
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    onClick={() => {
                      setAutoScroll(previous => !previous);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-slate-800 border-t border-slate-800"
                  >
                    <span>Auto-scroll</span>

                    <span
                      className={
                        autoScroll
                          ? 'text-indigo-400'
                          : 'text-slate-600'
                      }
                    >
                      {autoScroll
                        ? 'On'
                        : 'Off'}
                    </span>

                  </motion.button>


                  <motion.button
                    type="button"
                    disabled={
                      activeJobLogs.length ===
                      0
                    }
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    onClick={() => {
                      handleDownloadLogs();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2.5 text-left text-slate-300 hover:bg-slate-800 border-t border-slate-800 disabled:text-slate-600"
                  >
                    Download logs
                  </motion.button>


                  <motion.button
                    type="button"
                    disabled={
                      activeJobLogs.length ===
                      0
                    }
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    onClick={() => {
                      handleCopyLogs();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2.5 text-left text-slate-300 hover:bg-slate-800 border-t border-slate-800 disabled:text-slate-600"
                  >
                    {copiedLogs
                      ? 'Logs copied'
                      : 'Copy logs'}
                  </motion.button>


                  {canShareWithJarvis && (

                    <motion.button
                      type="button"
                      whileTap={{
                        scale: 0.985
                      }}
                      transition={
                        kaizenStateTransition
                      }
                      onClick={() => {
                        handleShareWithJarvis();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-3 py-2.5 text-left text-indigo-300 hover:bg-slate-800 border-t border-slate-800"
                    >
                      Share completed output with JARVIS
                    </motion.button>

                  )}


                  <motion.button
                    type="button"
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    onClick={() => {
                      clearLogs();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2.5 text-left text-rose-300 hover:bg-slate-800 border-t border-slate-800"
                  >
                    Clear terminal
                  </motion.button>

                </motion.div>

              )}

            </AnimatePresence>

          </div>
        </div>
      </div>

      {/* Terminal Stream Viewport */}
      <div
        ref={scrollRef}
            onDoubleClick={clearLogs}
            onTouchEnd={handleTerminalDoubleTap}
            title="Double-tap to clear terminal"
        className={`p-3 sm:p-4 overflow-y-auto bg-[#020817] ${
          fillAvailable
            ? 'flex-1 min-h-0'
            : maxHeight
        } space-y-0.5 selection:bg-indigo-600 selection:text-white leading-relaxed text-[11px] sm:text-xs`}
      >
        {activeJobLogs.length === 0 ? (
          <div className="h-full min-h-[150px] flex flex-col items-center justify-center text-center px-4">
            <Terminal className="w-5 h-5 text-slate-700 mb-2" />
            <div className="text-slate-500 text-[11px]">
              PowerShell ready.
            </div>
          </div>
        ) : (
          activeJobLogs.map((log) => {
            let textColor = 'text-slate-200';
            if (log.stream === 'stderr') textColor = 'text-rose-400 font-semibold';
            if (log.stream === 'system') textColor = 'text-indigo-400 font-medium';

            const timeStr = showTimestamps
              ? `[${new Date(log.timestamp).toLocaleTimeString()}] `
              : '';

            return (
              <div key={log.id} className="whitespace-pre-wrap break-all">
                {timeStr && <span className="text-slate-500 select-none">{timeStr}</span>}
                <span className={textColor}>{log.data}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Attached Report Quick Actions Bar (When 1 or more text artifacts exist) */}
      {showAttachedReportQuickActions && selectedArtifact && (
        <div className="border-t border-slate-800 bg-slate-900/95 px-3 sm:px-4 py-2.5 flex flex-col gap-2 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 min-w-0">
            {/* Left: Artifact Info / Multiple Artifact Selector */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-md bg-indigo-950/80 border border-indigo-700/50 flex items-center justify-center text-indigo-400 shrink-0">
                <FileText className="w-4 h-4" />
              </div>

              {artifacts.length > 1 ? (
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <span className="text-[10px] uppercase tracking-wider text-indigo-400 font-sans font-semibold shrink-0">
                    Reports ({artifacts.length}):
                  </span>
                  <div className="relative min-w-0 flex-1 max-w-[280px]">
                    <select
                      value={selectedArtifact.id}
                      onChange={e => setSelectedArtifactId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1 pr-6 truncate focus:ring-1 focus:ring-indigo-500 font-mono"
                    >
                      {artifacts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.filename} ({formatBytes(a.size)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-slate-400 font-sans font-medium shrink-0">
                      Report:
                    </span>
                    <span
                      className="text-xs font-mono font-medium text-slate-200 truncate"
                      title={selectedArtifact.filename}
                    >
                      {selectedArtifact.filename}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono shrink-0">
                      ({formatBytes(selectedArtifact.size)})
                    </span>
                  </div>
                </div>
              )}

              {/* Agent Offline Status */}
              {deviceStatus !== 'online' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-950/80 text-amber-300 border border-amber-800 shrink-0">
                  <WifiOff className="w-3 h-3" />
                  <span className="hidden sm:inline">Laptop Agent Offline</span>
                </span>
              )}
            </div>

            {/* Error Message if any */}
            {reportError && (
              <div className="inline-flex items-center gap-1 text-rose-400 text-[11px] font-sans font-medium px-2 py-0.5 rounded bg-rose-950/60 border border-rose-800/80 shrink-0">
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span>{reportError}</span>
              </div>
            )}

            {/* Right: Quick Action Buttons (Touch-friendly >= 44px on mobile) */}
            <div className="grid grid-cols-3 sm:flex sm:items-center gap-1.5 shrink-0 pt-1 sm:pt-0">
              {/* View Report */}
              <button
                type="button"
                onClick={handleViewReport}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-2 sm:py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-slate-600 transition-colors text-xs font-medium min-h-[44px] sm:min-h-0 active:scale-95 shadow-xs"
                title="View full report content"
              >
                <Eye className="w-3.5 h-3.5 text-indigo-400" />
                <span className="hidden sm:inline">View Report</span>
                <span className="sm:hidden">View</span>
              </button>

              {/* Copy Report */}
              <button
                type="button"
                onClick={handleCopyReport}
                disabled={copyingReport}
                className={`inline-flex items-center justify-center gap-1 px-2.5 py-2 sm:py-1 rounded-lg border transition-colors text-xs font-medium min-h-[44px] sm:min-h-0 active:scale-95 shadow-xs ${
                  copiedReport
                    ? 'bg-emerald-950/80 text-emerald-300 border-emerald-700 font-semibold'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700 hover:border-slate-600'
                }`}
                title="Copy full report text to clipboard"
              >
                {copyingReport ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                ) : copiedReport ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Copied report âœ“</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 text-slate-300" />
                    <span className="hidden sm:inline">Copy Report</span>
                    <span className="sm:hidden">Copy</span>
                  </>
                )}
              </button>

              {/* Download Report */}
              <button
                type="button"
                onClick={handleDownloadReport}
                disabled={downloadingReport}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-2 sm:py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-500 transition-colors text-xs font-medium min-h-[44px] sm:min-h-0 active:scale-95 shadow-xs disabled:opacity-50"
                title="Download report file"
              >
                {downloadingReport ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">Download Report</span>
                <span className="sm:hidden">Save</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Job Execution Output
          Shown only when THIS job has no registered file artifact. */}
      <AnimatePresence initial={false}>
        {showExecutionOutputCard && (
          <motion.div
            initial={{
              opacity: 0,
              height: 0,
              y: 5
            }}
            animate={{
              opacity: 1,
              height: 'auto',
              y: 0
            }}
            exit={{
              opacity: 0,
              height: 0,
              y: 4
            }}
            transition={
              kaizenAutoHeightTransition
            }
            className="
              shrink-0
              overflow-hidden
              border-t border-white/[0.07]
              bg-slate-900/90
              px-3 sm:px-4
              py-2.5
              backdrop-blur-xl
            "
          >
          <div className="flex flex-col gap-2">

            <div className="flex items-center justify-between gap-3 min-w-0">

              <div className="flex items-center gap-2 min-w-0">

                <div
                  className="
                    w-7 h-7
                    shrink-0
                    flex items-center justify-center
                    rounded-md
                    border border-indigo-700/40
                    bg-indigo-950/55
                    text-indigo-400
                  "
                >
                  <Terminal className="w-3.5 h-3.5" />
                </div>

                <div className="min-w-0">

                  <div
                    className="
                      text-[10px]
                      uppercase
                      tracking-[0.12em]
                      font-sans
                      font-semibold
                      text-slate-400
                    "
                  >
                    Execution Output
                  </div>

                  <div
                    className="
                      mt-0.5
                      flex items-center gap-1.5
                      min-w-0
                      font-mono
                      text-[10px]
                      text-slate-500
                    "
                  >
                    <span className="truncate">
                      Current job
                    </span>

                    <span>
                      Â·
                    </span>

                    <span className="shrink-0">
                      {formatBytes(terminalOutputBytes)}
                    </span>
                  </div>

                </div>

              </div>


              <div
                className="
                  hidden sm:flex
                  items-center
                  text-[10px]
                  text-emerald-400/80
                  font-sans
                "
              >
                Ready
              </div>

            </div>


            <div className="flex items-center justify-end gap-1">

              <span className="hidden sm:inline text-[9px] uppercase tracking-[0.12em] text-slate-600 mr-1">
                Console
              </span>


              <button
                type="button"
                onClick={handleCopyLogs}
                disabled={!terminalOutputText}
                title="Copy console output"
                aria-label="Copy console output"
                className={`
                  relative
                  w-8 h-8
                  inline-flex
                  items-center justify-center
                  rounded-md
                  border
                  transition-colors
                  ${
                    copiedLogs
                      ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/20'
                      : 'bg-transparent text-slate-400 border-transparent hover:text-slate-200 hover:bg-white/[0.05] hover:border-white/[0.08]'
                  }
                  disabled:opacity-25
                  disabled:pointer-events-none
                `}
              >
                {copiedLogs
                  ? (
                    <Check className="w-3.5 h-3.5" />
                  )
                  : (
                    <Copy className="w-3.5 h-3.5" />
                  )
                }
              </button>


              <button
                type="button"
                onClick={handleDownloadLogs}
                disabled={!terminalOutputText}
                title="Save console output"
                aria-label="Save console output"
                className="
                  w-8 h-8
                  inline-flex
                  items-center justify-center
                  rounded-md
                  border border-transparent
                  bg-transparent
                  text-slate-400
                  hover:text-slate-200
                  hover:bg-white/[0.05]
                  hover:border-white/[0.08]
                  disabled:opacity-25
                  disabled:pointer-events-none
                  transition-colors
                "
              >
                <Download className="w-3.5 h-3.5" />
              </button>


              {kaizenReportAvailable && (
                <>

                  <div className="h-4 w-px bg-white/[0.08] mx-1" />


                  <span className="hidden sm:inline text-[9px] uppercase tracking-[0.12em] text-slate-600 mr-1">
                    Report
                  </span>


                  <button
                    type="button"
                    onClick={
                      selectedArtifact
                        ? handleCopyReport
                        : () =>
                            void handleKaizenPathReport(
                              'copy'
                            )
                    }
                    disabled={
                      copyingReport ||
                      downloadingReport
                    }
                    title={
                      selectedArtifact
                        ? 'Copy generated report'
                        : kaizenDetectedReportPath ||
                          kaizenPathReportSnapshot ||
                          'Copy detected report'
                    }
                    aria-label="Copy generated report"
                    className={`
                      relative
                      w-8 h-8
                      inline-flex
                      items-center justify-center
                      rounded-md
                      border
                      transition-colors
                      ${
                        copiedReport
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/20'
                          : 'bg-transparent text-indigo-300 border-transparent hover:text-indigo-200 hover:bg-indigo-500/[0.08] hover:border-indigo-400/[0.12]'
                      }
                      disabled:opacity-35
                      disabled:pointer-events-none
                    `}
                  >
                    {copyingReport
                      ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      )
                      : copiedReport
                        ? (
                          <Check className="w-3.5 h-3.5" />
                        )
                        : (
                          <Copy className="w-3.5 h-3.5" />
                        )
                    }

                    <FileText className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 text-indigo-400" />
                  </button>


                  <button
                    type="button"
                    onClick={
                      selectedArtifact
                        ? handleDownloadReport
                        : () =>
                            void handleKaizenPathReport(
                              'download'
                            )
                    }
                    disabled={
                      copyingReport ||
                      downloadingReport
                    }
                    title="Save generated report"
                    aria-label="Save generated report"
                    className="
                      relative
                      w-8 h-8
                      inline-flex
                      items-center justify-center
                      rounded-md
                      border border-transparent
                      bg-transparent
                      text-indigo-300
                      hover:text-indigo-200
                      hover:bg-indigo-500/[0.08]
                      hover:border-indigo-400/[0.12]
                      disabled:opacity-35
                      disabled:pointer-events-none
                      transition-colors
                    "
                  >
                    {downloadingReport
                      ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      )
                      : (
                        <Download className="w-3.5 h-3.5" />
                      )
                    }

                    <FileText className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 text-indigo-400" />
                  </button>

                </>
              )}

            </div>

          </div>
          </motion.div>
        )}
      </AnimatePresence>

      {footer && (
        <div className="shrink-0 border-t border-white/[0.07] bg-slate-900/75 backdrop-blur-xl">
          {footer}
        </div>
      )}

      {/* Reusable File Viewer Modal */}
      <FileViewerModal
        artifact={selectedArtifact}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
      />
    </div>
  );
};





